import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { APP_CONFIG, SKIP_LOADING } from '@ribera-web/app-token';
import {
  catchError,
  map,
  Observable,
  of,
  throwError
 } from 'rxjs';
import {
  Country,
  Document,
  ApiResponse,
  UserClient,
  ProfileImageResponse,
  ProfileImageUrl,
  ProfileImageDeleteResponse
} from './user-data.models';

@Injectable({
  providedIn: 'root',
})
export class UserDataService {

  private readonly http = inject(HttpClient);

  private readonly config = inject(APP_CONFIG);

  private readonly inclubRiberaApiUrl = this.config.INCLUB_RIBERA_API;

  // Esta variable de entorno de DEV funciona para la edición de todos los campos menos para contraseña,
  // hay que actualizarlo con los últimos cambios de Local para que pueda actualizar tambien la contraseña
  private readonly accountMicroserviceApiUrl = this.config.ACCOUNT_MICROSERVICE_API;

  // la actualización de la contraseña solo funciona en el entorno local con esta ruta: http://localhost:8083
  //private readonly accountMicroserviceApiUrl= 'http://localhost:8083';

  getDocumentTypes(): Observable<Document[]> {
    return this.http.get<Document[]>(`${this.inclubRiberaApiUrl}/api/v1/documenttype/all`);
  }

  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(`${this.inclubRiberaApiUrl}/api/v1/countries`);
  }

  getUserClientById(id: number | string): Observable<UserClient> {
    const url = `${this.accountMicroserviceApiUrl}/api/v2/user-clients/${id}`;
    return this.http.get<ApiResponse<UserClient>>(url).pipe(map(res => res.data));
  }

  updateUserClient(id: number | string, userData: Partial<UserClient>): Observable<ApiResponse<UserClient>> {
    const url = `${this. accountMicroserviceApiUrl}/api/v2/user-clients/${id}`;

    return this.http.put<ApiResponse<UserClient>>(url, userData).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('[UserClientService] updateUserClient error', {
          status: error.status,
          statusText: error.statusText,
          url: url
        });

        return throwError(() => ({
          status: error.status,
          message: this.getErrorMessage(error),
          originalError: error
        }));
      })
    );
  }

  getUserProfileImage(userId: number | string): Observable<ProfileImageUrl> {
    const url = `${this.accountMicroserviceApiUrl}/api/v2/user-profiles/${userId}/image`;
    return this.http.get<ProfileImageResponse>(url, {context: new HttpContext().set(SKIP_LOADING, true)}).pipe(
      map(res => res.data),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of({ url: '/member/dashboard/Mask group.svg' });
        }

        return throwError(() => error);
      })
    );
  }

  uploadProfileImage(userId: number | string, file: File): Observable<ProfileImageUrl> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<ProfileImageResponse>(
      `${this.accountMicroserviceApiUrl}/api/v2/user-profiles/${userId}/upload`,
      formData
    ).pipe(
      map(res => res.data)
    );
  }

  updateProfileImage(userId: number | string, file: File): Observable<ProfileImageUrl> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.patch<ProfileImageResponse>(
      `${this.accountMicroserviceApiUrl}/api/v2/user-profiles/${userId}/image`,
      formData
    ).pipe(
      map(res => res.data)
    );
  }

  deleteProfileImage(userId: number | string): Observable<ProfileImageDeleteResponse> {
    return this.http.delete<ProfileImageDeleteResponse>(`${this.accountMicroserviceApiUrl}/api/v2/user-profiles/${userId}/image`);
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error?.message) {
      return error.error.message;
    }

    switch (error.status) {
      case 0:
        return 'No se pudo conectar con el servidor';
      case 400:
        return 'Datos inválidos enviados';
      case 401:
        return 'No autorizado';
      case 404:
        return 'Usuario no encontrado';
      case 500:
        return 'Error interno del servidor';
      default:
        return 'Error inesperado';
    }
  }
}
